import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { PageHeader, Spin, message, Select, Popconfirm } from "antd";
import { ThunderboltOutlined } from "@ant-design/icons";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
  ReactFlowProvider,
} from "react-flow-renderer";
import "react-flow-renderer/dist/style.css";
import { ButtonItem, SelectItem } from "../../../../Components/Items";
import NodoCanvas from "./Components/NodoCanvas";
import SeccionCanvas from "./Components/SeccionCanvas";
import RackCanvas from "./Components/RackCanvas";
import LineaGuiaCanvas from "./Components/LineaGuiaCanvas";
import BarraHerramientasMapa from "./Components/BarraHerramientasMapa";
import PanelPropiedadesNodo from "./Components/PanelPropiedadesNodo";
import PanelPropiedadesConexion from "./Components/PanelPropiedadesConexion";
import PanelPropiedadesSeccion from "./Components/PanelPropiedadesSeccion";
import PanelPropiedadesRack from "./Components/PanelPropiedadesRack";
import AsistenteMapaPersonalizado from "./Components/AsistenteMapaPersonalizado";
import ModalCrearMapa from "./Components/ModalCrearMapa";
import ModalAgregarSeccion from "./Components/ModalAgregarSeccion";
import ModalAgregarPasillo from "./Components/ModalAgregarPasillo";
import ModalAgregarRack from "./Components/ModalAgregarRack";
import ModalAgregarNodo from "./Components/ModalAgregarNodo";
import ModalValidacionMapa from "./Components/ModalValidacionMapa";

const { Option } = Select;

const nodeTypes = { punto: NodoCanvas, seccion: SeccionCanvas, rack: RackCanvas, guia: LineaGuiaCanvas };
const PREFIJO_NODO = "nodo-";
const PREFIJO_SECCION = "seccion-";
const PREFIJO_RACK = "rack-";
const NODO_MITAD_TAMANO = 17; // mitad del círculo de 34px de NodoCanvas.js - ver uso abajo
const UMBRAL_ALINEACION = 25; // px lógicos - qué tan cerca debe estar de otro nodo/rack para sugerir alinearse
const TAMANO_LINEA_GUIA = 6000; // largo de la línea guía, suficientemente grande para cruzar cualquier mapa

// Nodos y racks tienen un Handle por lado (top/right/bottom/left) - en vez de dejar que React Flow use
// siempre el primero (lo que hacía que toda línea entrara por arriba), se elige el lado de cada extremo
// más cercano al otro punto, según la posición relativa de sus coordenadas.
const LADO_OPUESTO = { top: "bottom", bottom: "top", left: "right", right: "left" };
const elegirLadoHaciaDestino = (origen, destino) => {
  const dx = destino.x - origen.x;
  const dy = destino.y - origen.y;
  if (Math.abs(dx) > Math.abs(dy)) return dx >= 0 ? "right" : "left";
  return dy >= 0 ? "bottom" : "top";
};

const MapaAlmacenEditor = ({ empresaId, headersToken, proyectoId, history }) => {
  const [cargando, setCargando] = useState(false);
  const [mapaAlmacen, setMapaAlmacen] = useState(null);
  const [secciones, setSecciones] = useState([]);
  const [nodosCrudos, setNodosCrudos] = useState([]);
  const [conexionesCrudas, setConexionesCrudas] = useState([]);
  const [racksCrudos, setRacksCrudos] = useState([]);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const [modo, setModo] = useState(null); // null | agregando_nodo | eligiendo_inicio | eligiendo_fin
  const [datosNodoPendiente, setDatosNodoPendiente] = useState(null);

  const [nodoSeleccionado, setNodoSeleccionado] = useState(null);
  const [conexionSeleccionada, setConexionSeleccionada] = useState(null);
  const [seccionSeleccionada, setSeccionSeleccionada] = useState(null);
  const [rackSeleccionado, setRackSeleccionado] = useState(null);

  const [modalCrearMapaVisible, setModalCrearMapaVisible] = useState(false);
  const [modalAgregarSeccionVisible, setModalAgregarSeccionVisible] = useState(false);
  const [modalAgregarPasilloVisible, setModalAgregarPasilloVisible] = useState(false);
  const [modalAgregarNodoVisible, setModalAgregarNodoVisible] = useState(false);
  const [modalAgregarRackVisible, setModalAgregarRackVisible] = useState(false);
  const [modalValidacion, setModalValidacion] = useState(null);
  const [nivelEjemplo, setNivelEjemplo] = useState("basico");
  const [generandoEjemplo, setGenerandoEjemplo] = useState(false);
  const [asistenteVisible, setAsistenteVisible] = useState(false);
  const [guiaAlineacion, setGuiaAlineacion] = useState({ x: null, y: null }); // coords lógicas donde mostrar la línea guía mientras se arrastra
  const [seleccionIds, setSeleccionIds] = useState([]); // ids (con prefijo) actualmente seleccionados en el lienzo, para copiar/eliminar con teclado

  const instanciaFlow = useRef(null);
  const wrapperFlow = useRef(null);
  const portapapeles = useRef(null); // { nodos: [...], racks: [...] } - lo que se copió con Ctrl+C
  const ultimaPosicionNodo = useRef(null); // último punto colocado en la sesión de dibujo actual (para alinear horizontal/vertical)
  const nodosColocadosEnSesion = useRef(0); // cuántos nodos ya se colocaron en esta sesión (el 1ro usa el código tal cual, los siguientes se numeran)

  const cargarMapa = useCallback(async () => {
    setCargando(true);
    try {
      const res = await axios.post("mapaAlmacen/selectMapaAlmacen", { empresaId, proyectoId }, { headers: headersToken });
      if (res.data.success === 2) {
        setMapaAlmacen(res.data.mapaAlmacen);
        setSecciones(res.data.secciones || []);
        setNodosCrudos(res.data.nodos || []);
        setConexionesCrudas(res.data.conexiones || []);
        setRacksCrudos(res.data.racks || []);
      }
    } catch (error) {
      console.error("Error al cargar el mapa del almacén:", error);
      message.error("No se pudo cargar el mapa del almacén");
    } finally {
      setCargando(false);
    }
  }, [empresaId, proyectoId, headersToken]);

  useEffect(() => {
    cargarMapa();
  }, [cargarMapa]);

  const guardarPosicionSeccion = useCallback(
    async (seccionId, params) => {
      const seccion = secciones.find((valorSeccion) => String(valorSeccion._id) === seccionId);
      if (!seccion) return;
      const limitesVisuales = {
        x: params.x ?? seccion.limitesVisuales?.x,
        y: params.y ?? seccion.limitesVisuales?.y,
        ancho: params.width ?? seccion.limitesVisuales?.ancho,
        alto: params.height ?? seccion.limitesVisuales?.alto,
      };
      try {
        await axios.post(
          "mapaAlmacen/editarSeccionAlmacen",
          {
            _id: seccionId,
            codigo: seccion.codigo,
            nombre: seccion.nombre,
            tipo: seccion.tipo,
            prioridadPicking: seccion.prioridadPicking,
            limitesVisuales,
          },
          { headers: headersToken }
        );
        setSecciones((prev) => prev.map((s) => (String(s._id) === seccionId ? { ...s, limitesVisuales } : s)));
      } catch (error) {
        console.error("Error al guardar la posición de la sección:", error);
        message.error("Falló el guardado de la sección");
      }
    },
    [secciones, headersToken]
  );

  const guardarPosicionRack = useCallback(
    async (rackId, params) => {
      const rack = racksCrudos.find((valorRack) => String(valorRack._id) === rackId);
      if (!rack) return;
      const patch = {
        x: params.x ?? rack.x,
        y: params.y ?? rack.y,
        ancho: params.width ?? rack.ancho,
        alto: params.height ?? rack.alto,
      };
      try {
        await axios.post(
          "rack/editarRack",
          {
            _id: rackId,
            codigo: rack.codigo,
            nombre: rack.nombre,
            numeroColumnas: rack.numeroColumnas,
            numeroNiveles: rack.numeroNiveles,
            numeroProfundidades: rack.numeroProfundidades,
            ...patch,
          },
          { headers: headersToken }
        );
        setRacksCrudos((prev) => prev.map((r) => (String(r._id) === rackId ? { ...r, ...patch } : r)));
      } catch (error) {
        console.error("Error al guardar la posición del rack:", error);
        message.error("Falló el guardado del rack");
      }
    },
    [racksCrudos, headersToken]
  );

  // Reconstruye los nodes/edges de React Flow cada vez que cambian los datos del backend
  useEffect(() => {
    if (!mapaAlmacen) {
      setNodes([]);
      setEdges([]);
      return;
    }

    const nodosFlow = [
      ...secciones.map((valorSeccion) => ({
        id: PREFIJO_SECCION + valorSeccion._id,
        type: "seccion",
        position: { x: valorSeccion.limitesVisuales?.x || 0, y: valorSeccion.limitesVisuales?.y || 0 },
        style: { width: valorSeccion.limitesVisuales?.ancho || 240, height: valorSeccion.limitesVisuales?.alto || 200 },
        zIndex: -1,
        data: {
          ...valorSeccion,
          anchoActual: valorSeccion.limitesVisuales?.ancho || 240,
          altoActual: valorSeccion.limitesVisuales?.alto || 200,
          onResize: (params) => guardarPosicionSeccion(String(valorSeccion._id), params),
        },
      })),
      ...nodosCrudos.map((valorNodo) => ({
        id: PREFIJO_NODO + valorNodo._id,
        type: "punto",
        // valorNodo.x/y es el punto LÓGICO (centro del nodo - ahí es donde tocan las líneas, donde se
        // alinean los racks, etc). React Flow posiciona los nodos por su esquina superior izquierda, así
        // que hay que restar la mitad del círculo para que quede realmente centrado sobre ese punto.
        position: { x: valorNodo.x - NODO_MITAD_TAMANO, y: valorNodo.y - NODO_MITAD_TAMANO },
        zIndex: 1,
        data: {
          ...valorNodo,
          esNodoInicio: String(mapaAlmacen.nodoInicioPickingId || "") === String(valorNodo._id),
          esNodoFin: String(mapaAlmacen.nodoFinPickingId || "") === String(valorNodo._id),
        },
      })),
      ...racksCrudos.map((valorRack) => ({
        id: PREFIJO_RACK + valorRack._id,
        type: "rack",
        position: { x: valorRack.x, y: valorRack.y },
        style: { width: valorRack.ancho, height: valorRack.alto },
        zIndex: 2,
        data: {
          ...valorRack,
          anchoActual: valorRack.ancho,
          altoActual: valorRack.alto,
          onResize: (params) => guardarPosicionRack(String(valorRack._id), params),
        },
      })),
    ];

    const nodoPorId = new Map(nodosCrudos.map((valorNodo) => [String(valorNodo._id), { x: valorNodo.x, y: valorNodo.y }]));
    const rackPorId = new Map(
      racksCrudos.map((valorRack) => [String(valorRack._id), { x: valorRack.x + valorRack.ancho / 2, y: valorRack.y + valorRack.alto / 2 }])
    );

    const edgesCirculacion = conexionesCrudas.map((valorConexion) => {
      const coordOrigen = nodoPorId.get(String(valorConexion.nodoOrigenId));
      const coordDestino = nodoPorId.get(String(valorConexion.nodoDestinoId));
      const ladoOrigen = coordOrigen && coordDestino ? elegirLadoHaciaDestino(coordOrigen, coordDestino) : "top";
      const ladoDestino = LADO_OPUESTO[ladoOrigen];
      return {
        id: "conexion-" + valorConexion._id,
        source: PREFIJO_NODO + valorConexion.nodoOrigenId,
        target: PREFIJO_NODO + valorConexion.nodoDestinoId,
        sourceHandle: ladoOrigen,
        targetHandle: `${ladoDestino}-target`,
        markerEnd: valorConexion.tipoDireccion === "unidireccional" ? { type: MarkerType.ArrowClosed } : undefined,
        style: { stroke: valorConexion.bloqueada ? "#ef4444" : "#8a93a6", strokeDasharray: valorConexion.bloqueada ? "4 2" : undefined },
        label: `${Number(valorConexion.distanciaMetros || 0).toFixed(1)} m`,
        data: { ...valorConexion },
      };
    });

    // Vínculos rack<->nodo: NO son tramos caminables, solo indican desde dónde se alcanza el rack.
    // Se dibujan punteados y en el color del rack para distinguirlos de las conexiones reales.
    const edgesVinculo = racksCrudos.flatMap((valorRack) => {
      const coordRack = rackPorId.get(String(valorRack._id));
      return (valorRack.nodosAccesoIds || []).map((nodoId) => {
        const coordNodo = nodoPorId.get(String(nodoId));
        const ladoRack = coordRack && coordNodo ? elegirLadoHaciaDestino(coordRack, coordNodo) : "top";
        const ladoNodo = LADO_OPUESTO[ladoRack];
        return {
          id: `vinculo-${valorRack._id}-${nodoId}`,
          source: PREFIJO_RACK + valorRack._id,
          target: PREFIJO_NODO + nodoId,
          sourceHandle: ladoRack,
          targetHandle: `${ladoNodo}-target`,
          style: { stroke: "#BF8D6F", strokeDasharray: "3 3" },
          data: { esVinculo: true },
        };
      });
    });

    setNodes(nodosFlow);
    setEdges([...edgesCirculacion, ...edgesVinculo]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapaAlmacen, secciones, nodosCrudos, conexionesCrudas, racksCrudos]);

  // Las líneas guía son "nodos" fantasma solo para dibujar (no se guardan) - se agregan aparte de `nodes`
  // para no ensuciar el estado real, y así se benefician gratis del pan/zoom que ya aplica React Flow.
  const nodesConGuias = useMemo(() => {
    const guias = [];
    if (guiaAlineacion.x !== null) {
      guias.push({
        id: "guia-x",
        type: "guia",
        position: { x: guiaAlineacion.x - 0.5, y: -TAMANO_LINEA_GUIA / 2 },
        style: { width: 1, height: TAMANO_LINEA_GUIA },
        draggable: false,
        selectable: false,
        connectable: false,
        zIndex: 999,
        data: {},
      });
    }
    if (guiaAlineacion.y !== null) {
      guias.push({
        id: "guia-y",
        type: "guia",
        position: { x: -TAMANO_LINEA_GUIA / 2, y: guiaAlineacion.y - 0.5 },
        style: { width: TAMANO_LINEA_GUIA, height: 1 },
        draggable: false,
        selectable: false,
        connectable: false,
        zIndex: 999,
        data: {},
      });
    }
    return guias.length > 0 ? [...nodes, ...guias] : nodes;
  }, [nodes, guiaAlineacion]);

  // Selección actual del lienzo (clic normal de React Flow, o rectángulo con Shift) - se usa para
  // copiar/pegar/eliminar con teclado (ver el listener de teclado más abajo).
  const onSelectionChange = useCallback(({ nodes: nodosSeleccionados }) => {
    setSeleccionIds((nodosSeleccionados || []).map((n) => n.id));
  }, []);

  const copiarSeleccion = useCallback(() => {
    const nodosCopiados = nodosCrudos.filter((n) => seleccionIds.includes(PREFIJO_NODO + n._id));
    const racksCopiados = racksCrudos.filter((r) => seleccionIds.includes(PREFIJO_RACK + r._id));
    if (nodosCopiados.length === 0 && racksCopiados.length === 0) return;
    portapapeles.current = { nodos: nodosCopiados, racks: racksCopiados };
    message.success(`${nodosCopiados.length + racksCopiados.length} elemento(s) copiado(s) - Ctrl+V para pegar`);
  }, [seleccionIds, nodosCrudos, racksCrudos]);

  const pegarPortapapeles = useCallback(async () => {
    const copia = portapapeles.current;
    if (!copia || (!copia.nodos.length && !copia.racks.length) || !mapaAlmacen) return;
    const DESPLAZAMIENTO = 40;

    for (const nodo of copia.nodos) {
      const x = nodo.x + DESPLAZAMIENTO;
      const y = nodo.y + DESPLAZAMIENTO;
      const codigo = `${nodo.codigo}-COPIA`;
      try {
        const res = await axios.post(
          "mapaAlmacen/agregarNodoMapa",
          { empresaId, proyectoId, mapaAlmacenId: mapaAlmacen._id, seccionAlmacenId: nodo.seccionAlmacenId, codigo, nombre: nodo.nombre, tipo: nodo.tipo, x, y },
          { headers: headersToken }
        );
        if (res.data.success === 2) {
          setNodosCrudos((prev) => [
            ...prev,
            { _id: res.data.nodoMapaId, mapaAlmacenId: mapaAlmacen._id, seccionAlmacenId: nodo.seccionAlmacenId, codigo, nombre: nodo.nombre, tipo: nodo.tipo, x, y, activo: true },
          ]);
        } else {
          message.error(res.data.error || "No se pudo pegar un nodo");
        }
      } catch (error) {
        console.error("Error al pegar el nodo:", error);
        message.error("No se pudo pegar un nodo");
      }
    }

    for (const rack of copia.racks) {
      const x = rack.x + DESPLAZAMIENTO;
      const y = rack.y + DESPLAZAMIENTO;
      const codigo = `${rack.codigo}-COPIA`;
      try {
        const res = await axios.post(
          "rack/agregarRack",
          {
            empresaId,
            proyectoId,
            mapaAlmacenId: mapaAlmacen._id,
            seccionAlmacenId: rack.seccionAlmacenId,
            codigo,
            nombre: rack.nombre,
            x,
            y,
            ancho: rack.ancho,
            alto: rack.alto,
            numeroColumnas: rack.numeroColumnas,
            numeroNiveles: rack.numeroNiveles,
            numeroProfundidades: rack.numeroProfundidades,
          },
          { headers: headersToken }
        );
        if (res.data.success === 2) {
          setRacksCrudos((prev) => [
            ...prev,
            {
              _id: res.data.rackId,
              mapaAlmacenId: mapaAlmacen._id,
              seccionAlmacenId: rack.seccionAlmacenId,
              codigo,
              nombre: rack.nombre,
              x,
              y,
              ancho: rack.ancho,
              alto: rack.alto,
              numeroColumnas: rack.numeroColumnas,
              numeroNiveles: rack.numeroNiveles,
              numeroProfundidades: rack.numeroProfundidades,
              nodosAccesoIds: [],
              activo: true,
            },
          ]);
        } else {
          message.error(res.data.error || "No se pudo pegar un rack");
        }
      } catch (error) {
        console.error("Error al pegar el rack:", error);
        message.error("No se pudo pegar un rack");
      }
    }
  }, [empresaId, proyectoId, mapaAlmacen, headersToken]);

  const eliminarSeleccion = useCallback(async () => {
    for (const id of seleccionIds) {
      if (id.startsWith(PREFIJO_NODO)) {
        const nodoId = id.replace(PREFIJO_NODO, "");
        try {
          const res = await axios.post("mapaAlmacen/eliminarNodoMapa", { _id: nodoId }, { headers: headersToken });
          if (res.data.success === 2) setNodosCrudos((prev) => prev.filter((n) => String(n._id) !== nodoId));
          else message.error(res.data.error || "No se pudo eliminar un nodo");
        } catch (error) {
          console.error("Error al eliminar el nodo:", error);
          message.error("No se pudo eliminar un nodo");
        }
      } else if (id.startsWith(PREFIJO_SECCION)) {
        const seccionId = id.replace(PREFIJO_SECCION, "");
        try {
          const res = await axios.post("mapaAlmacen/eliminarSeccionAlmacen", { _id: seccionId }, { headers: headersToken });
          if (res.data.success === 2) setSecciones((prev) => prev.filter((s) => String(s._id) !== seccionId));
          else message.error(res.data.error || "No se pudo eliminar una sección");
        } catch (error) {
          console.error("Error al eliminar la sección:", error);
          message.error("No se pudo eliminar una sección");
        }
      } else if (id.startsWith(PREFIJO_RACK)) {
        const rackId = id.replace(PREFIJO_RACK, "");
        try {
          const res = await axios.post("rack/eliminarRack", { _id: rackId }, { headers: headersToken });
          if (res.data.success === 2) setRacksCrudos((prev) => prev.filter((r) => String(r._id) !== rackId));
          else message.error(res.data.error || "No se pudo eliminar un rack");
        } catch (error) {
          console.error("Error al eliminar el rack:", error);
          message.error("No se pudo eliminar un rack");
        }
      }
    }
    setSeleccionIds([]);
  }, [seleccionIds, headersToken]);

  // Atajos de teclado: Ctrl/Cmd+C copia la selección, Ctrl/Cmd+V la pega desplazada, Supr/Backspace la
  // elimina. Se ignoran si el foco está en un campo de texto (para no interferir con copiar/pegar texto
  // normal en los modales/drawers).
  useEffect(() => {
    const manejarTeclado = (event) => {
      const enCampoTexto = ["INPUT", "TEXTAREA"].includes(event.target.tagName) || event.target.isContentEditable;
      if (enCampoTexto) return;

      const teclaCtrl = event.ctrlKey || event.metaKey;
      if (teclaCtrl && event.key.toLowerCase() === "c") {
        event.preventDefault();
        copiarSeleccion();
      } else if (teclaCtrl && event.key.toLowerCase() === "v") {
        event.preventDefault();
        pegarPortapapeles();
      } else if ((event.key === "Delete" || event.key === "Backspace") && seleccionIds.length > 0) {
        event.preventDefault();
        eliminarSeleccion();
      }
    };
    window.addEventListener("keydown", manejarTeclado);
    return () => window.removeEventListener("keydown", manejarTeclado);
  }, [copiarSeleccion, pegarPortapapeles, eliminarSeleccion, seleccionIds]);

  //Genera una estructura de ejemplo completa (secciones+nodos+conexiones) y reparte en sus puntos de
  //acceso las ubicaciones que el almacén YA tenga dadas de alta (no crea ubicaciones nuevas). Si ya había
  //un mapa activo, lo reemplaza (lo desactiva, no lo borra).
  const generarEjemplo = async () => {
    setGenerandoEjemplo(true);
    try {
      const res = await axios.post(
        "mapaAlmacen/generarMapaEjemplo",
        { empresaId, proyectoId, nivel: nivelEjemplo },
        { headers: headersToken }
      );
      if (res.data.success === 2) {
        message.success(
          `Mapa de ejemplo generado: ${res.data.totalSecciones} sección(es), ${res.data.totalPasillos} pasillo(s), ${res.data.totalNodos} nodos, ${res.data.totalConexiones} conexiones, ${res.data.totalRacks} racks, ${res.data.totalUbicacionesAsignadas}/${res.data.totalUbicacionesDisponibles} ubicaciones asignadas`
        );
        cargarMapa();
      } else {
        message.error(res.data.error || "No se pudo generar el mapa de ejemplo");
      }
    } catch (error) {
      console.error("Error al generar el mapa de ejemplo:", error);
      message.error("No se pudo generar el mapa de ejemplo");
    } finally {
      setGenerandoEjemplo(false);
    }
  };

  const iniciarAgregarNodo = () => setModalAgregarNodoVisible(true);

  const confirmarDatosNodoYEsperarClic = (datosNodo) => {
    setDatosNodoPendiente(datosNodo);
    setModalAgregarNodoVisible(false);
    ultimaPosicionNodo.current = null;
    nodosColocadosEnSesion.current = 0;
    setModo("agregando_nodo");
  };

  // Modo dibujo: cada clic coloca un nodo y SIGUE en este modo (no se resetea) para poner varios rápido,
  // hasta que el usuario cancela desde la barra de herramientas. A partir del 2do clic de la sesión, el
  // nuevo punto se alinea completamente horizontal o completamente vertical respecto al anterior (el eje
  // con menor desplazamiento del mouse se "pega" al valor previo), y el código se numera automáticamente.
  const onPaneClick = useCallback(
    async (event) => {
      if (modo !== "agregando_nodo" || !instanciaFlow.current || !datosNodoPendiente || !wrapperFlow.current) return;
      const bounds = wrapperFlow.current.getBoundingClientRect();
      const posicionCruda = instanciaFlow.current.project({ x: event.clientX - bounds.left, y: event.clientY - bounds.top });

      let posicion = posicionCruda;
      if (ultimaPosicionNodo.current) {
        const dx = posicionCruda.x - ultimaPosicionNodo.current.x;
        const dy = posicionCruda.y - ultimaPosicionNodo.current.y;
        posicion =
          Math.abs(dx) >= Math.abs(dy)
            ? { x: posicionCruda.x, y: ultimaPosicionNodo.current.y }
            : { x: ultimaPosicionNodo.current.x, y: posicionCruda.y };
      }

      const codigo =
        nodosColocadosEnSesion.current === 0
          ? datosNodoPendiente.codigo
          : `${datosNodoPendiente.codigo}-${nodosColocadosEnSesion.current + 1}`;

      try {
        const res = await axios.post(
          "mapaAlmacen/agregarNodoMapa",
          {
            empresaId,
            proyectoId,
            mapaAlmacenId: mapaAlmacen._id,
            seccionAlmacenId: datosNodoPendiente.seccionAlmacenId,
            codigo,
            nombre: datosNodoPendiente.nombre,
            tipo: datosNodoPendiente.tipo,
            x: posicion.x,
            y: posicion.y,
          },
          { headers: headersToken }
        );
        if (res.data.success === 2) {
          setNodosCrudos((prev) => [
            ...prev,
            {
              _id: res.data.nodoMapaId,
              mapaAlmacenId: mapaAlmacen._id,
              seccionAlmacenId: datosNodoPendiente.seccionAlmacenId,
              codigo,
              nombre: datosNodoPendiente.nombre,
              tipo: datosNodoPendiente.tipo,
              x: posicion.x,
              y: posicion.y,
              activo: true,
            },
          ]);
          ultimaPosicionNodo.current = posicion;
          nodosColocadosEnSesion.current += 1;
        } else {
          message.error(res.data.error || "Falló el guardado del nodo");
        }
      } catch (error) {
        console.error("Error al crear el nodo:", error);
        message.error("Falló el guardado del nodo");
      }
    },
    [modo, datosNodoPendiente, empresaId, proyectoId, mapaAlmacen, headersToken]
  );

  const onConnect = useCallback(
    async (params) => {
      const origenEsRack = params.source.startsWith(PREFIJO_RACK);
      const destinoEsRack = params.target.startsWith(PREFIJO_RACK);

      // Rack <-> nodo: es un vínculo (asociación), no un tramo caminable - ver rack/vincularNodoARack.
      if (origenEsRack || destinoEsRack) {
        if (origenEsRack && destinoEsRack) return; // no tiene sentido vincular un rack con otro rack
        const rackId = (origenEsRack ? params.source : params.target).replace(PREFIJO_RACK, "");
        const nodoId = (origenEsRack ? params.target : params.source).replace(PREFIJO_NODO, "");
        try {
          const res = await axios.post("rack/vincularNodoARack", { rackId, nodoId }, { headers: headersToken });
          if (res.data.success === 2) {
            setRacksCrudos((prev) =>
              prev.map((r) => (String(r._id) === rackId ? { ...r, nodosAccesoIds: [...(r.nodosAccesoIds || []), nodoId] } : r))
            );
          } else {
            message.error(res.data.error || "Falló el guardado del vínculo");
          }
        } catch (error) {
          console.error("Error al vincular el rack:", error);
          message.error("Falló el guardado del vínculo");
        }
        return;
      }

      if (!params.source.startsWith(PREFIJO_NODO) || !params.target.startsWith(PREFIJO_NODO)) return;
      const nodoOrigenId = params.source.replace(PREFIJO_NODO, "");
      const nodoDestinoId = params.target.replace(PREFIJO_NODO, "");
      try {
        const res = await axios.post(
          "mapaAlmacen/agregarConexionMapa",
          {
            empresaId,
            proyectoId,
            mapaAlmacenId: mapaAlmacen._id,
            nodoOrigenId,
            nodoDestinoId,
            tipoDireccion: "bidireccional",
            tipoRecorrido: "pasillo",
            distanciaManual: false,
          },
          { headers: headersToken }
        );
        if (res.data.success === 2) {
          setConexionesCrudas((prev) => [
            ...prev,
            {
              _id: res.data.conexionMapaId,
              mapaAlmacenId: mapaAlmacen._id,
              nodoOrigenId,
              nodoDestinoId,
              tipoDireccion: "bidireccional",
              tipoRecorrido: "pasillo",
              distanciaMetros: res.data.distanciaMetros,
              distanciaManual: false,
              activa: true,
            },
          ]);
        } else {
          message.error(res.data.error || "Falló el guardado de la conexión");
        }
      } catch (error) {
        console.error("Error al crear la conexión:", error);
        message.error("Falló el guardado de la conexión");
      }
    },
    [empresaId, proyectoId, mapaAlmacen, headersToken]
  );

  // Mientras se arrastra un nodo O un rack, busca si su centro quedó cerca del eje X o Y del centro de
  // algún OTRO nodo o rack (dentro de UMBRAL_ALINEACION) y, si es así, lo pega a ese valor exacto y
  // muestra una línea guía - así es mucho más fácil dejar todo perfectamente alineado sin hacerlo a ojo.
  const onNodeDrag = useCallback(
    (_event, node) => {
      const esNodo = node.id.startsWith(PREFIJO_NODO);
      const esRack = node.id.startsWith(PREFIJO_RACK);
      if (!esNodo && !esRack) return;

      const idPropio = esNodo ? node.id.replace(PREFIJO_NODO, "") : node.id.replace(PREFIJO_RACK, "");
      let anchoPropio = 0;
      let altoPropio = 0;
      if (esRack) {
        const rackPropio = racksCrudos.find((r) => String(r._id) === idPropio);
        anchoPropio = rackPropio?.ancho ?? 70;
        altoPropio = rackPropio?.alto ?? 70;
      }
      const centroX = esNodo ? node.position.x + NODO_MITAD_TAMANO : node.position.x + anchoPropio / 2;
      const centroY = esNodo ? node.position.y + NODO_MITAD_TAMANO : node.position.y + altoPropio / 2;

      let mejorX = null;
      let mejorY = null;
      let distX = UMBRAL_ALINEACION;
      let distY = UMBRAL_ALINEACION;

      nodosCrudos.forEach((otro) => {
        if (esNodo && String(otro._id) === idPropio) return;
        const dx = Math.abs(otro.x - centroX);
        if (dx < distX) {
          distX = dx;
          mejorX = otro.x;
        }
        const dy = Math.abs(otro.y - centroY);
        if (dy < distY) {
          distY = dy;
          mejorY = otro.y;
        }
      });

      racksCrudos.forEach((otro) => {
        if (esRack && String(otro._id) === idPropio) return;
        const centroOtroX = otro.x + otro.ancho / 2;
        const centroOtroY = otro.y + otro.alto / 2;
        const dx = Math.abs(centroOtroX - centroX);
        if (dx < distX) {
          distX = dx;
          mejorX = centroOtroX;
        }
        const dy = Math.abs(centroOtroY - centroY);
        if (dy < distY) {
          distY = dy;
          mejorY = centroOtroY;
        }
      });

      setGuiaAlineacion({ x: mejorX, y: mejorY });

      if (mejorX !== null || mejorY !== null) {
        const nuevaPosicion = esNodo
          ? {
              x: mejorX !== null ? mejorX - NODO_MITAD_TAMANO : node.position.x,
              y: mejorY !== null ? mejorY - NODO_MITAD_TAMANO : node.position.y,
            }
          : {
              x: mejorX !== null ? mejorX - anchoPropio / 2 : node.position.x,
              y: mejorY !== null ? mejorY - altoPropio / 2 : node.position.y,
            };
        setNodes((prev) => prev.map((n) => (n.id === node.id ? { ...n, position: nuevaPosicion } : n)));
      }
    },
    [nodosCrudos, racksCrudos, setNodes]
  );

  const onNodeDragStop = useCallback(
    async (_event, node) => {
      setGuiaAlineacion({ x: null, y: null });
      if (node.id.startsWith(PREFIJO_SECCION)) {
        await guardarPosicionSeccion(node.id.replace(PREFIJO_SECCION, ""), { x: node.position.x, y: node.position.y });
        return;
      }
      if (node.id.startsWith(PREFIJO_RACK)) {
        await guardarPosicionRack(node.id.replace(PREFIJO_RACK, ""), { x: node.position.x, y: node.position.y });
        return;
      }
      if (node.id.startsWith(PREFIJO_NODO)) {
        const nodoId = node.id.replace(PREFIJO_NODO, "");
        const nodo = nodosCrudos.find((valorNodo) => String(valorNodo._id) === nodoId);
        if (!nodo) return;
        // node.position es la esquina superior izquierda del círculo (ver el offset -NODO_MITAD_TAMANO al
        // construir nodesFlow) - hay que devolverla al punto lógico (centro) antes de guardar.
        const x = node.position.x + NODO_MITAD_TAMANO;
        const y = node.position.y + NODO_MITAD_TAMANO;
        try {
          await axios.post(
            "mapaAlmacen/editarNodoMapa",
            {
              _id: nodoId,
              codigo: nodo.codigo,
              nombre: nodo.nombre,
              tipo: nodo.tipo,
              seccionAlmacenId: nodo.seccionAlmacenId,
              x,
              y,
              transicion: nodo.transicion,
              metadata: nodo.metadata,
            },
            { headers: headersToken }
          );
          setNodosCrudos((prev) => prev.map((n) => (String(n._id) === nodoId ? { ...n, x, y } : n)));
        } catch (error) {
          console.error("Error al mover el nodo:", error);
          message.error("Falló el guardado del nodo");
        }
      }
    },
    [nodosCrudos, headersToken, guardarPosicionSeccion, guardarPosicionRack]
  );

  // Clic sencillo: solo sirve para elegir el nodo de inicio/fin de picking (modo activado desde la
  // barra). Fuera de esos modos no hace nada más - abrir el panel de propiedades requiere doble clic
  // (ver onNodeDoubleClick) para poder seleccionar/arrastrar sin que se abra el drawer sin querer.
  const onNodeClick = useCallback(
    async (_event, node) => {
      if (modo !== "eligiendo_inicio" && modo !== "eligiendo_fin") return;
      if (!node.id.startsWith(PREFIJO_NODO)) return;
      const nodoId = node.id.replace(PREFIJO_NODO, "");

      try {
        const res = await axios.post(
          "mapaAlmacen/editarMapaAlmacen",
          {
            _id: mapaAlmacen._id,
            nombre: mapaAlmacen.nombre,
            ancho: mapaAlmacen.ancho,
            alto: mapaAlmacen.alto,
            unidadCoordenadas: mapaAlmacen.unidadCoordenadas,
            pixelesPorMetro: mapaAlmacen.pixelesPorMetro,
            nodoInicioPickingId: modo === "eligiendo_inicio" ? nodoId : mapaAlmacen.nodoInicioPickingId,
            nodoFinPickingId: modo === "eligiendo_fin" ? nodoId : mapaAlmacen.nodoFinPickingId,
          },
          { headers: headersToken }
        );
        if (res.data.success === 2) {
          message.success(modo === "eligiendo_inicio" ? "Nodo de inicio definido" : "Nodo de fin definido");
          setMapaAlmacen((prev) => ({
            ...prev,
            nodoInicioPickingId: modo === "eligiendo_inicio" ? nodoId : prev.nodoInicioPickingId,
            nodoFinPickingId: modo === "eligiendo_fin" ? nodoId : prev.nodoFinPickingId,
          }));
        } else {
          message.error(res.data.error || "Falló el guardado");
        }
      } catch (error) {
        console.error("Error al definir el nodo de inicio/fin:", error);
        message.error("Falló el guardado");
      } finally {
        setModo(null);
      }
    },
    [modo, mapaAlmacen, headersToken]
  );

  // Doble clic: abre el panel de propiedades del elemento (nodo/sección/rack).
  const onNodeDoubleClick = useCallback(
    (_event, node) => {
      if (node.id.startsWith(PREFIJO_SECCION)) {
        setSeccionSeleccionada(secciones.find((valorSeccion) => String(valorSeccion._id) === node.id.replace(PREFIJO_SECCION, "")));
        return;
      }
      if (node.id.startsWith(PREFIJO_RACK)) {
        setRackSeleccionado(racksCrudos.find((valorRack) => String(valorRack._id) === node.id.replace(PREFIJO_RACK, "")));
        return;
      }
      if (node.id.startsWith(PREFIJO_NODO)) {
        const nodoId = node.id.replace(PREFIJO_NODO, "");
        const nodo = nodosCrudos.find((valorNodo) => String(valorNodo._id) === nodoId);
        if (nodo) setNodoSeleccionado(nodo);
      }
    },
    [secciones, racksCrudos, nodosCrudos]
  );

  const onEdgeClick = useCallback(
    (_event, edge) => {
      setConexionSeleccionada(edge.data);
    },
    []
  );

  const validarMapa = async () => {
    try {
      const res = await axios.post("mapaAlmacen/validarMapaAlmacen", { mapaAlmacenId: mapaAlmacen._id }, { headers: headersToken });
      if (res.data.success === 2) {
        setModalValidacion(res.data);
      } else {
        message.error("No se pudo validar el mapa");
      }
    } catch (error) {
      console.error("Error al validar el mapa:", error);
      message.error("No se pudo validar el mapa");
    }
  };

  if (!mapaAlmacen && !cargando) {
    return (
      <>
        <PageHeader
          title="Editor de mapa avanzado"
          onBack={() => history.goBack()}
          style={{ border: "1px solid rgb(235,237,240)", backgroundColor: "white", marginBottom: 10 }}
        />
        <div style={{ background: "#fff", borderRadius: 14, padding: 40, textAlign: "center" }}>
          <p style={{ color: "#8a93a6", marginBottom: 16 }}>Este almacén todavía no tiene un mapa configurado.</p>
          <ButtonItem type="primary" verde onClick={() => setModalCrearMapaVisible(true)}>
            Crear mapa
          </ButtonItem>
          <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid #eef0f6", display: "flex", justifyContent: "center", gap: 8, alignItems: "center" }}>
            <span style={{ color: "#8a93a6" }}>o genera uno de ejemplo:</span>
            <SelectItem width={160} value={nivelEjemplo} onChange={setNivelEjemplo}>
              <Option value="basico">Básico</Option>
              <Option value="medio">Medio</Option>
              <Option value="avanzado">Avanzado</Option>
              <Option value="doble_cara">Pasillo doble cara</Option>
              <Option value="complejo">Complejo (cruces + doble cara)</Option>
            </SelectItem>
            <ButtonItem  className="botonAgregar" mostaza icon={<ThunderboltOutlined />} loading={generandoEjemplo} onClick={generarEjemplo}>
              Generar ejemplo
            </ButtonItem>
            <span style={{ color: "#8a93a6" }}>o</span>
            <ButtonItem className="botonAgregar" siena onClick={() => setAsistenteVisible(true)}>Asistente personalizado</ButtonItem>
          </div>
        </div>

        <AsistenteMapaPersonalizado
          visible={asistenteVisible}
          onClose={() => setAsistenteVisible(false)}
          empresaId={empresaId}
          proyectoId={proyectoId}
          headersToken={headersToken}
          onGenerado={cargarMapa}
        />

        <ModalCrearMapa
          visible={modalCrearMapaVisible}
          onClose={() => setModalCrearMapaVisible(false)}
          empresaId={empresaId}
          proyectoId={proyectoId}
          headersToken={headersToken}
          onCreado={cargarMapa}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={mapaAlmacen ? `Editor de mapa — ${mapaAlmacen.nombre}` : "Editor de mapa avanzado"}
        onBack={() => history.goBack()}
        style={{ border: "1px solid rgb(235,237,240)", backgroundColor: "white", marginBottom: 10 }}
        extra={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ color: "#8a93a6", fontSize: 12 }}>Reemplazar con mapa de ejemplo:</span>
            <SelectItem width={150} size="small" value={nivelEjemplo} onChange={setNivelEjemplo}>
              <Option value="basico">Básico</Option>
              <Option value="medio">Medio</Option>
              <Option value="avanzado">Avanzado</Option>
              <Option value="doble_cara">Pasillo doble cara</Option>
              <Option value="complejo">Complejo (cruces + doble cara)</Option>
            </SelectItem>
            <Popconfirm
              title="Esto reemplaza el mapa activo actual (no se borra, queda desactivado). ¿Continuar?"
              onConfirm={generarEjemplo}
              okText="Generar"
              cancelText="Cancelar"
            >
              <ButtonItem mostaza size="small" className="botonAgregar" icon={<ThunderboltOutlined />} loading={generandoEjemplo}>
                Generar ejemplo
              </ButtonItem>
            </Popconfirm>
            <ButtonItem siena size="small"  className="botonAgregar" onClick={() => setAsistenteVisible(true)}>
              Asistente personalizado
            </ButtonItem>
          </div>
        }
      />
      <div style={{ background: "#fff", borderRadius: 14, padding: 16 }}>
        <BarraHerramientasMapa
          modo={modo}
          onAgregarSeccion={() => setModalAgregarSeccionVisible(true)}
          onAgregarPasillo={() => setModalAgregarPasilloVisible(true)}
          onAgregarRack={() => setModalAgregarRackVisible(true)}
          onAgregarNodo={iniciarAgregarNodo}
          onElegirInicio={() => setModo("eligiendo_inicio")}
          onElegirFin={() => setModo("eligiendo_fin")}
          onValidar={validarMapa}
          onCancelarModo={() => {
            setModo(null);
            setDatosNodoPendiente(null);
            ultimaPosicionNodo.current = null;
            nodosColocadosEnSesion.current = 0;
          }}
        />

        <AsistenteMapaPersonalizado
          visible={asistenteVisible}
          onClose={() => setAsistenteVisible(false)}
          empresaId={empresaId}
          proyectoId={proyectoId}
          headersToken={headersToken}
          onGenerado={cargarMapa}
        />

        <Spin spinning={cargando}>
          <div ref={wrapperFlow} style={{ height: "70vh", border: "1px solid #eef0f6", borderRadius: 8 }}>
            <ReactFlowProvider>
              <ReactFlow
                nodes={nodesConGuias}
                edges={edges}
                nodeTypes={nodeTypes}
                connectionMode="loose"
                selectionKeyCode="Shift"
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onNodeDrag={onNodeDrag}
                onNodeDragStop={onNodeDragStop}
                onNodeClick={onNodeClick}
                onNodeDoubleClick={onNodeDoubleClick}
                onSelectionChange={onSelectionChange}
                onEdgeClick={onEdgeClick}
                onPaneClick={onPaneClick}
                onInit={(instance) => (instanciaFlow.current = instance)}
                fitView
              >
                <Background />
                <Controls />
                <MiniMap />
              </ReactFlow>
            </ReactFlowProvider>
          </div>
        </Spin>
      </div>

      <PanelPropiedadesNodo
        visible={!!nodoSeleccionado}
        onClose={() => setNodoSeleccionado(null)}
        nodo={nodoSeleccionado}
        secciones={secciones}
        headersToken={headersToken}
        onGuardado={() => {
          setNodoSeleccionado(null);
          cargarMapa();
        }}
        onEliminado={() => {
          setNodoSeleccionado(null);
          cargarMapa();
        }}
      />

      <PanelPropiedadesConexion
        visible={!!conexionSeleccionada}
        onClose={() => setConexionSeleccionada(null)}
        conexion={conexionSeleccionada}
        headersToken={headersToken}
        onGuardado={() => {
          setConexionSeleccionada(null);
          cargarMapa();
        }}
        onEliminado={() => {
          setConexionSeleccionada(null);
          cargarMapa();
        }}
      />

      <PanelPropiedadesSeccion
        visible={!!seccionSeleccionada}
        onClose={() => setSeccionSeleccionada(null)}
        seccion={seccionSeleccionada}
        headersToken={headersToken}
        onGuardado={() => {
          setSeccionSeleccionada(null);
          cargarMapa();
        }}
        onEliminado={() => {
          setSeccionSeleccionada(null);
          cargarMapa();
        }}
      />

      <PanelPropiedadesRack
        visible={!!rackSeleccionado}
        onClose={() => setRackSeleccionado(null)}
        rack={rackSeleccionado}
        nodosCrudos={nodosCrudos}
        headersToken={headersToken}
        onGuardado={() => {
          setRackSeleccionado(null);
          cargarMapa();
        }}
        onEliminado={() => {
          setRackSeleccionado(null);
          cargarMapa();
        }}
      />

      <ModalAgregarSeccion
        visible={modalAgregarSeccionVisible}
        onClose={() => setModalAgregarSeccionVisible(false)}
        empresaId={empresaId}
        proyectoId={proyectoId}
        mapaAlmacenId={mapaAlmacen?._id}
        headersToken={headersToken}
        onCreado={(seccionNueva) => setSecciones((prev) => [...prev, seccionNueva])}
      />

      <ModalAgregarPasillo
        visible={modalAgregarPasilloVisible}
        onClose={() => setModalAgregarPasilloVisible(false)}
        empresaId={empresaId}
        proyectoId={proyectoId}
        mapaAlmacenId={mapaAlmacen?._id}
        headersToken={headersToken}
        secciones={secciones}
        onCreado={({ nodos, conexion }) => {
          setNodosCrudos((prev) => [...prev, ...nodos]);
          setConexionesCrudas((prev) => [...prev, conexion]);
        }}
      />

      <ModalAgregarRack
        visible={modalAgregarRackVisible}
        onClose={() => setModalAgregarRackVisible(false)}
        empresaId={empresaId}
        proyectoId={proyectoId}
        mapaAlmacenId={mapaAlmacen?._id}
        headersToken={headersToken}
        onCreado={(rackNuevo) => setRacksCrudos((prev) => [...prev, rackNuevo])}
      />

      <ModalAgregarNodo
        visible={modalAgregarNodoVisible}
        onClose={() => setModalAgregarNodoVisible(false)}
        secciones={secciones}
        onConfirmar={confirmarDatosNodoYEsperarClic}
      />

      <ModalValidacionMapa
        visible={!!modalValidacion}
        onClose={() => setModalValidacion(null)}
        datos={modalValidacion}
      />
    </>
  );
};

const mapStateToProps = (state, ownProps) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  proyectoId: ownProps.location?.state?.proyectoId,
});

export default connect(mapStateToProps)(MapaAlmacenEditor);
